import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  type QAStatusType,
  type QACategoryType,
  type QAEntityType,
} from '@intone/shared';

import { useQAFeedback, useUpdateQAFeedback, useCreateQAFeedback } from '../model/useQAFeedback';

interface QAFeedbackSectionParams {
  entityId: string;
  entityType: QAEntityType;
  userId: string;
  authorName?: string;
}

export function useQAFeedbackSection({ entityId, entityType, userId, authorName }: QAFeedbackSectionParams) {
  const { data: qaFeedback, isLoading: qaLoading, isSuccess, error } = useQAFeedback(entityId, entityType);

  const isTicketCreated = !!qaFeedback;

  const [status, setStatus] = useState<QAStatusType | ''>('');
  const [category, setCategory] = useState<QACategoryType | ''>('');

  const updateMutation = useUpdateQAFeedback();
  const createMutation = useCreateQAFeedback();

  const isCreateTicketPending = createMutation.isPending;
  const isFeedbackPending = qaLoading || updateMutation.isPending || createMutation.isPending;

  // Reset form when entity changes
  useEffect(() => {
    if (isSuccess && qaFeedback) {
      setStatus((qaFeedback.status as QAStatusType) || '');
      setCategory(qaFeedback.category as QACategoryType ?? '');
    } else {
      setStatus('');
      setCategory('');
    }
  }, [isSuccess, qaFeedback])

  function handleCreateTicket(comment: string) {
    if (!entityId || !status || !category || !comment) return;

    createMutation.mutate(
      {
        entity_id: entityId,
        entity_type: entityType,
        feedback: {
          id: '',
          entity_id: entityId,
          entity_type: entityType,
          status,
          category,
          updated_time: new Date(),
        },
        comment: {
          id: 1,
          entity_id: entityId,
          entity_type: entityType,
          author_id: userId || 'Unknown',
          author_name: authorName || 'Unknown',
          message: comment,
          created_at: new Date(),
          updated_at: new Date(),
        }
      },
      {
        onSuccess: () => {
          toast.success('QA ticket created', { id: 'qa-create' })
        },
        onError: (err: Error) => {
          toast.error(err.message || 'Failed to create QA ticket')
        },
      }
    )
  }

  function handleCategoryChange(value: string) {
    setCategory(value as QACategoryType)
    if (isTicketCreated) {
      updateMutation.mutate(
        {
          entity_id: entityId,
          entity_type: entityType,
          status,
          category: value,
        },
        {
          onSuccess: () => {
            toast.success('Category updated', { id: 'qa-autosave-category' })
          },
          onError: (err: Error) => {
            toast.error(err.message || 'Failed to update category', { id: 'qa-autosave-category-failed' })
          },
        }
      )
    }
  }

  function handleStatusChange(value: string) {
    setStatus(value as QAStatusType)
    if (isTicketCreated) {
      updateMutation.mutate(
        {
          entity_id: entityId,
          entity_type: entityType,
          status: value,
          category,
        },
        {
          onSuccess: () => {
            toast.success('Status updated', { id: 'qa-autosave-status' })
          },
          onError: (err: Error) => {
            toast.error(err.message || 'Failed to update status', { id: 'qa-autosave-status-failed' })
          },
        }
      )
    }
  }

  return {
    category,
    error,
    isCreateTicketPending,
    isFeedbackPending,
    isTicketCreated,
    qaFeedback,
    status,

    handleCreateTicket,
    handleCategoryChange,
    handleStatusChange,
  };
}
